import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { IMAGE_MAP } from "./revealMap";

gsap.registerPlugin(ScrollTrigger);

// ===== helpers =====
function getSection(sectionId) {
  return document.getElementById(sectionId);
}

function ensureDecorWrap(parentEl) {
  if (!parentEl) return null;

  let decorWrap = parentEl.querySelector(":scope > .js-decor-wrap");
  if (!decorWrap) {
    decorWrap = document.createElement("div");
    decorWrap.className = "js-decor-wrap";
    decorWrap.setAttribute("aria-hidden", "true");
    parentEl.prepend(decorWrap);

    // ★ 親要素に class を付与
    parentEl.classList.add("js-has-decor-wrap");
  }
  return decorWrap;
}

function addImagesToDecorWrap(sectionId) {
  const sectionEl = getSection(sectionId);
  if (!sectionEl) return;

  if (sectionEl.classList.contains("anim-completed")) return;

  const itemsPC = IMAGE_MAP?.[sectionId]?.pc ?? [];
  const itemsSP = IMAGE_MAP?.[sectionId]?.sp ?? [];
  if (!itemsPC.length && !itemsSP.length) return;

  // target無し用（セクション直下）
  const sectionWrap = ensureDecorWrap(sectionEl);
  if (!sectionWrap) return;

  // ★ 増殖防止：このセクション内のdecorを全削除（wrapごと）
  // 「wrap共有」を前提にするので、.js-decor を丸ごと消すのが一番安全
  sectionEl.querySelectorAll(".js-decor").forEach((n) => n.remove());

  const refreshOnImgLoad = (img) => {
    if (!img || img.complete) return;
    img.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });
  };

  // classNameごとのwrapを作る（存在すれば再利用）
  function getOrCreateDecorWrap({ className, target }) {
    // 既に同名wrapがあればそれを使う（PC→SP共有の要）
    const existing = sectionEl.querySelector(`.js-decor.${className}`);
    if (existing) return existing;

    // 無ければ「親wrap」を決める（target優先）
    let parentWrap = sectionWrap;
    if (target) {
      const targetEl = sectionEl.querySelector(target);
      const targetWrap = ensureDecorWrap(targetEl);
      if (targetWrap) parentWrap = targetWrap;
    }

    const wrap = document.createElement("div");
    wrap.className = `js-decor ${className}`;
    parentWrap.appendChild(wrap);
    return wrap;
  }

  function upsertImgIntoWrap(wrap, { src }, device, sectionIdForInit) {
    const cls = device === "sp" ? "is-sp" : "is-pc";

    // 同じdeviceのimgが既にあれば差し替え（増殖防止）
    wrap.querySelectorAll(`img.${cls}`).forEach((n) => n.remove());

    const img = document.createElement("img");
    img.src = src;
    img.alt = "";
    img.loading = "lazy";
    img.classList.add(cls);
    refreshOnImgLoad(img);

    wrap.appendChild(img);

    // 初期位置（wrap自体に当てる）
    if (device === "pc") {
      gsap.set(wrap, { autoAlpha: 0, x: 100 });
    } else {
      const baseX = sectionIdForInit === "page-hero" ? 40 : 50;
      gsap.set(wrap, { autoAlpha: 0, x: baseX });
    }
  }

  // ① PCを先に入れる（wrapの配置位置をPC基準で確定させる）
  itemsPC.forEach((item) => {
    const wrap = getOrCreateDecorWrap(item);
    upsertImgIntoWrap(wrap, item, "pc", sectionId);
  });

  // ② SPは「同名wrapがあるならそこへ」（無ければ target/section基準で新規）
  itemsSP.forEach((item) => {
    const wrap = getOrCreateDecorWrap(item); // ここでPC wrapがあれば必ずそれを拾う
    upsertImgIntoWrap(wrap, item, "sp", sectionId);
  });

  // hero テキスト初期化
  if (sectionId === "page-hero") {
    gsap.set(
      sectionEl.querySelectorAll(".page-hero-subtitle, .page-hero-title"),
      { autoAlpha: 0, y: 15 }
    );
  }

  requestAnimationFrame(() => {
    requestAnimationFrame(() => ScrollTrigger.refresh());
  });
}

function clearStyles(elSelector) {
  const el = document.querySelector(elSelector);
  if (!el) return;
  const els = document.querySelectorAll(elSelector);
  els.forEach((e) => {
    e.classList.add("anim-completed");
    gsap.set(e, { clearProps: "all" });
  });
};

function fireVmIfNeeded(vm, st) {
  if (vm.dataset.vmEntered === "1") return;
  if (st && st.progress > 0) {
    vm.dataset.vmEntered = "1";
    visionMissionEnter(vm);
  }
}

// ===== animations (scoped) =====
function topHeroTimeline(sectionEl) {
  const tl = gsap.timeline();

  const subtitle = sectionEl.querySelector(".page-hero-subtitle");
  const title = sectionEl.querySelector(".page-hero-title");

  if (subtitle) {
    tl.to(subtitle, { autoAlpha: 1, y: 0, duration: 0.6, ease: "power1.in", onComplete: () => clearStyles('.page-hero-subtitle') }, 0);
  };
  if (title) {
    tl.to(title, { autoAlpha: 1, y: 0, duration: 0.6, ease: "power1.in", onComplete: () => clearStyles('.page-hero-title') }, "-=0.5");
  }

  // decor（このセクション内だけ！）
  const d1 = sectionEl.querySelector(".decor-hero-01");
  const d2 = sectionEl.querySelector(".decor-hero-02");
  const d3 = sectionEl.querySelector(".decor-hero-03");

  // テキストの後に確実に開始したいので 1枚目は ">"
  if (d1) {
    tl.to(d1, { autoAlpha: 1, x: 0, duration: 1, ease: "power3.out", onComplete: () => clearStyles('.decor-hero-01') }, ">");
  }
  if (d2) {
    tl.to(d2, { autoAlpha: 1, x: 0, duration: 1, ease: "power3.out", onComplete: () => clearStyles('.decor-hero-02') }, "-=0.1");
  }
  if (d3) {
    tl.to(d3, { autoAlpha: 1, x: 0, duration: 1, ease: "power3.out", onComplete: () => clearStyles('.decor-hero-03') }, "-=0.1");
  }
  sectionEl.classList.add("anim-completed");
  return tl;
}

function serviceTimeline(sectionEl) {
  const tl = gsap.timeline();

  const d3 = sectionEl.querySelector(".decor-service-03");
  const d2 = sectionEl.querySelector(".decor-service-02");
  const d1 = sectionEl.querySelector(".decor-service-01");

  if (d3) {
    tl.to(d3, { autoAlpha: 1, x: 0, duration: 0.8, ease: "power2.out", onComplete: () => clearStyles('.decor-service-03') }, 0);
    d3.classList.add("anim-completed");
  }
  if (d2) {
    tl.to(d2, { autoAlpha: 1, x: 0, duration: 0.8, ease: "power2.out", onComplete: () => clearStyles('.decor-service-02') }, "-=0.4");
  }
  if (d1) {
    tl.to(d1, { autoAlpha: 1, x: 0, duration: 0.8, ease: "power2.out", onComplete: () => clearStyles('.decor-service-01') }, "-=0.4");
  }
  sectionEl.classList.add("anim-completed");
  return tl;
}

function visionMissionEnter(sectionEl) {
  const d1 = sectionEl.querySelector(".decor-vision-mission-01");
  if (!d1) return;
  gsap.to(d1, { autoAlpha: 1, x: 0, duration: 0.6, ease: "power2.out", onComplete: () => clearStyles('.decor-vision-mission-01') });
}

function visionMissionLeave(sectionEl) {
  const d2 = sectionEl.querySelector(".decor-vision-mission-02");
  if (!d2) return;
  gsap.to(d2, { autoAlpha: 1, x: 0, duration: 0.6, ease: "power2.out", onComplete: () => clearStyles('.decor-vision-mission-02') });
  sectionEl.classList.add("anim-completed");
}

function newsAnim(sectionEl) {
  const d1 = sectionEl.querySelector(".decor-news-01");
  if (!d1) return;
  // ここはxで統一（yにするなら初期もyにする）
  gsap.to(d1, { autoAlpha: 1, x: 0, duration: 0.6, ease: "power2.out", onComplete: () => clearStyles('.decor-news-01') });
  sectionEl.classList.add("anim-completed");
}

function contactTimeline(sectionEl) {
  const tl = gsap.timeline();

  const d1 = sectionEl.querySelector(".decor-contact-01");
  const d2 = sectionEl.querySelector(".decor-contact-02");
  if (d1) {
    tl.to(d1, { autoAlpha: 1, x: 0, duration: 0.6, ease: "power2.out", onComplete: () => clearStyles('.decor-contact-01') }, 0);
  }
  if (d2) {
    tl.to(d2, { autoAlpha: 1, x: 0, duration: 0.8, ease: "power2.out", onComplete: () => clearStyles('.decor-contact-02') }, "+=0.4");
  }
  sectionEl.classList.add("anim-completed");
  return tl;
}

// ===== init =====
document.addEventListener("DOMContentLoaded", () => {
  addImagesToDecorWrap("page-hero");
  addImagesToDecorWrap("section-service");
  addImagesToDecorWrap("section-vision-mission");
  addImagesToDecorWrap("section-news");
  addImagesToDecorWrap("section-contact");

  // TOP（1回だけ）
  const hero = getSection("page-hero");
  if (hero) {
    setTimeout(() => topHeroTimeline(hero), 300);
  }

  // service（1回だけ）
  const service = getSection("section-service");
  if (service) {
    ScrollTrigger.create({
      trigger: service,
      start: "top 50%",
      once: true,
      onEnter: () => serviceTimeline(service),
    });
  }

  // vision-mission（1回だけ：1枚目）
  const vm = getSection("section-vision-mission");
  if (vm) {
    const st = ScrollTrigger.create({
      trigger: vm,
      start: "top 50%",
      end: "bottom 100%",
      onEnter: () => {
        if (vm.dataset.vmEntered === "1") return;
        vm.dataset.vmEntered = "1";
        visionMissionEnter(vm);
      },
      onLeave: () => visionMissionLeave(vm),
    });
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
      fireVmIfNeeded(vm, st);
    });
  }
  const scrollTween = gsap.to(".connectivity-as-air-animation-content", {
    xPercent: -50,
    duration: 15,
    ease: "none",
    repeat: -1,
    paused: true,
  });

  ScrollTrigger.create({
    trigger: ".connectivity-as-air-animation-content",
    start: "top bottom",
    end: "bottom top",
    onEnter: () => scrollTween.play(),
    onEnterBack: () => scrollTween.play(),
    onLeave: () => scrollTween.pause(),
    onLeaveBack: () => scrollTween.pause(),
  });

  // news（1回だけ）
  const news = getSection("section-news");
  if (news) {
    ScrollTrigger.create({
      trigger: news,
      start: "top 50%",
      once: true,
      onEnter: () => newsAnim(news),
    });
  }

  // contact（1回だけ）
  const contact = getSection("section-contact");
  if (contact) {
    ScrollTrigger.create({
      trigger: contact,
      start: "top 50%",
      once: true,
      onEnter: () => contactTimeline(contact),
    });
  }
});